import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, CheckCircle, AlertCircle, Calendar, Square, CheckSquare } from 'lucide-react';
import api from '../../services/api';

const CalendrierInterventions = ({ 
  beneficiaireId, 
  dateSelectionnee, 
  onSelectCreneau,
  multipleSelection = false,  // NOUVEAU
  selectedCreneaux = [],      // NOUVEAU
  onSelectMultiple            // NOUVEAU
}) => {
  const [interventions, setInterventions] = useState([]);
  const [plagesHoraires, setPlagesHoraires] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(dateSelectionnee ? new Date(dateSelectionnee) : new Date()));
  const [loading, setLoading] = useState(false);
  const [localSelected, setLocalSelected] = useState([]);

  const joursNoms = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const joursAbr = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  // Synchroniser avec selectedCreneaux du parent
  useEffect(() => {
    setLocalSelected(selectedCreneaux || []);
  }, [selectedCreneaux]);

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  }

  useEffect(() => {
    if (beneficiaireId) {
      loadData();
    }
  }, [beneficiaireId, currentWeekStart]);

  const loadData = async () => {
    setLoading(true);
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const interventionsRes = await api.get('/interventions', {
        params: {
          beneficiaireId,
          dateDebut: currentWeekStart.toISOString().split('T')[0],
          dateFin: weekEnd.toISOString().split('T')[0]
        }
      });

      const beneficiaireRes = await api.get(`/beneficiaires/${beneficiaireId}`);

      setInterventions(interventionsRes.data);
      setPlagesHoraires(beneficiaireRes.data.plagesHoraires || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const getInterventionsDuJour = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return interventions.filter(inter => {
      const interDateStr = new Date(inter.date).toISOString().split('T')[0];
      return interDateStr === dateStr;
    });
  };

  const getPlagesDuJour = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const jourSemaine = date.getDay();

    return plagesHoraires.filter(plage => {
      if (plage.typeRecurrence === 'unique') {
        const plageDate = new Date(plage.dateSpecifique).toISOString().split('T')[0];
        return plageDate === dateStr;
      }
      if (plage.typeRecurrence === 'hebdomadaire') {
        return plage.jourSemaine === jourSemaine;
      }
      if (plage.typeRecurrence === 'quotidien') {
        const debut = new Date(plage.dateDebut);
        const fin = new Date(plage.dateFin);
        return date >= debut && date <= fin;
      }
      return false;
    });
  };

  const getCreneauxLibres = (date) => {
    const plages = getPlagesDuJour(date);
    const interventionsDuJour = getInterventionsDuJour(date);
    const creneauxLibres = [];

    plages.forEach(plage => {
      const aIntervention = interventionsDuJour.some(inter => {
        return inter.heureDebut === plage.heureDebut && inter.heureFin === plage.heureFin;
      });

      if (!aIntervention) {
        creneauxLibres.push({
          heureDebut: plage.heureDebut,
          heureFin: plage.heureFin,
          date: date,
          typeRecurrence: plage.typeRecurrence
        });
      }
    });

    return creneauxLibres;
  };

  // NOUVEAU : Vérifier si créneau est sélectionné
  const isCreneauSelected = (creneau) => {
    return localSelected.some(c => 
      c.date === creneau.date.toISOString().split('T')[0] &&
      c.heureDebut === creneau.heureDebut &&
      c.heureFin === creneau.heureFin
    );
  };

  // NOUVEAU : Toggle sélection créneau
  const handleToggleCreneau = (creneau) => {
    const creneauData = {
      date: creneau.date.toISOString().split('T')[0],
      heureDebut: creneau.heureDebut,
      heureFin: creneau.heureFin
    };

    if (multipleSelection) {
      // Mode sélection multiple
      const isSelected = isCreneauSelected(creneau);
      let newSelection;
      
      if (isSelected) {
        // Désélectionner
        newSelection = localSelected.filter(c => 
          !(c.date === creneauData.date && 
            c.heureDebut === creneauData.heureDebut && 
            c.heureFin === creneauData.heureFin)
        );
      } else {
        // Sélectionner
        newSelection = [...localSelected, creneauData];
      }
      
      setLocalSelected(newSelection);
      if (onSelectMultiple) {
        onSelectMultiple(newSelection);
      }
    } else {
      // Mode sélection simple (ancien comportement)
      if (onSelectCreneau) {
        onSelectCreneau(creneauData);
      }
    }
  };

  const renderJour = (dayOffset) => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + dayOffset);
    
    const interventionsDuJour = getInterventionsDuJour(date);
    const creneauxLibres = getCreneauxLibres(date);
    const jourSemaine = date.getDay();
    const dateStr = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    const isToday = new Date().toDateString() === date.toDateString();
    const isSelected = dateSelectionnee && new Date(dateSelectionnee).toDateString() === date.toDateString();

    return (
      <div 
        key={dayOffset}
        className={`bg-white rounded-xl border-2 p-3 sm:p-4 transition-all ${
          isSelected 
            ? 'border-blue-500 shadow-lg' 
            : isToday 
            ? 'border-green-500' 
            : 'border-gray-200'
        }`}
      >
        {/* Header jour */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className={`font-bold text-sm sm:text-base ${isToday ? 'text-green-600' : 'text-gray-800'}`}>
              {joursAbr[jourSemaine]}
            </div>
            <div className="text-xs text-gray-500">{dateStr}</div>
          </div>
          {isToday && (
            <div className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-bold">
              Aujourd'hui
            </div>
          )}
        </div>

        {/* Interventions planifiées */}
        {interventionsDuJour.length > 0 && (
          <div className="space-y-2 mb-3">
            <div className="text-xs font-bold text-gray-600 mb-1">
              ✅ Planifiées ({interventionsDuJour.length})
            </div>
            {interventionsDuJour.map((inter, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-lg p-2"
              >
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-gray-800 truncate">
                      {inter.heureDebut} - {inter.heureFin}
                    </div>
                    <div className="text-[10px] text-gray-600 truncate">
                      {inter.type}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Créneaux libres */}
        {creneauxLibres.length > 0 ? (
          <div className="space-y-2">
            <div className="text-xs font-bold text-blue-600 mb-1">
              🕐 Créneaux libres ({creneauxLibres.length})
            </div>
            {creneauxLibres.map((creneau, idx) => {
              const isSelected = isCreneauSelected(creneau);
              
              return (
                <button
                  key={idx}
                  onClick={() => handleToggleCreneau(creneau)}
                  className={`w-full rounded-lg p-2 transition-all active:scale-95 border-2 ${
                    isSelected
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 border-purple-600 shadow-lg'
                      : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-300 hover:border-blue-500'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {multipleSelection && (
                      isSelected ? (
                        <CheckSquare size={16} className="text-white flex-shrink-0" />
                      ) : (
                        <Square size={16} className="text-blue-600 flex-shrink-0" />
                      )
                    )}
                    {!multipleSelection && (
                      <CheckCircle size={14} className="text-blue-600 flex-shrink-0" />
                    )}
                    <div className="flex-1 text-left">
                      <div className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-gray-800'}`}>
                        {creneau.heureDebut} → {creneau.heureFin}
                      </div>
                      <div className={`text-[10px] ${isSelected ? 'text-purple-100' : 'text-gray-600'}`}>
                        {multipleSelection 
                          ? (isSelected ? 'Sélectionné' : 'Cliquez pour sélectionner')
                          : 'Cliquez pour sélectionner'
                        }
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : interventionsDuJour.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <AlertCircle size={20} className="mx-auto mb-1 text-gray-400" />
            <div className="text-xs text-gray-500">Aucune plage définie</div>
          </div>
        ) : null}
      </div>
    );
  };

  if (!beneficiaireId) {
    return (
      <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4 text-center">
        <Calendar size={32} className="mx-auto mb-2 text-blue-500" />
        <p className="text-sm text-blue-700 font-bold">
          Sélectionnez un bénéficiaire pour voir les créneaux disponibles
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white border-2 border-gray-200 rounded-xl p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-500">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header navigation */}
      <div className="flex items-center justify-between bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-3 sm:p-4">
        <button
          onClick={() => navigateWeek(-1)}
          className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-all active:scale-95"
        >
          <ChevronLeft size={20} />
        </button>
        
        <div className="text-center">
          <div className="font-bold text-sm sm:text-base">
            Semaine du {currentWeekStart.getDate()} {currentWeekStart.toLocaleDateString('fr-FR', { month: 'long' })}
          </div>
          <div className="text-xs text-blue-100">
            {multipleSelection 
              ? `${localSelected.length} créneau${localSelected.length > 1 ? 'x' : ''} sélectionné${localSelected.length > 1 ? 's' : ''}`
              : 'Interventions et créneaux disponibles'
            }
          </div>
        </div>
        
        <button
          onClick={() => navigateWeek(1)}
          className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-all active:scale-95"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Légende */}
      <div className="flex flex-wrap gap-2 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span className="text-gray-600">Planifiée</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-500 rounded"></div>
          <span className="text-gray-600">Libre</span>
        </div>
        {multipleSelection && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded"></div>
            <span className="text-gray-600">Sélectionné</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-gray-300 rounded"></div>
          <span className="text-gray-600">Aucune plage</span>
        </div>
      </div>

      {/* Grille semaine */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 sm:gap-3">
        {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => renderJour(dayOffset))}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
        <strong>💡 Astuce :</strong> {multipleSelection 
          ? 'Cliquez sur plusieurs créneaux pour créer plusieurs interventions en une fois.'
          : 'Cliquez sur un créneau libre pour remplir automatiquement la date et les horaires.'
        }
      </div>
    </div>
  );
};

export default CalendrierInterventions;
